import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Languages } from "lucide-react";
import { englishTwin, getLang, hindiTwin, setLang, useLang } from "@/lib/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let _hi = null, _hiAt = 0, _hp = null, _hpAt = 0, _pc = null, _pcAt = 0;
export const loadHindiSlugs = () => (_hi && Date.now() - _hiAt < 300000) ? _hi : (_hiAt = Date.now(), _hi = fetch(`${API}/public/i18n/hi/services`).then((r) => (r.ok ? r.json() : { slugs: [] })).catch(() => ({ slugs: [] })));
export const loadHindiPostSlugs = () => (_hp && Date.now() - _hpAt < 300000) ? _hp : (_hpAt = Date.now(), _hp = fetch(`${API}/public/i18n/hi/posts`).then((r) => (r.ok ? r.json() : { slugs: [] })).catch(() => ({ slugs: [] })));
export const loadHindiPodcastSlugs = () => (_pc && Date.now() - _pcAt < 300000) ? _pc : (_pcAt = Date.now(), _pc = fetch(`${API}/public/i18n/hi/podcast`).then((r) => (r.ok ? r.json() : { slugs: [] })).catch(() => ({ slugs: [] })));
let _pages = {};
const pageExists = (page) => { const c = _pages[page]; if (c && Date.now() - c.at < 300000) return c.p; const p = fetch(`${API}/public/i18n/hi/pages/${page}`).then((r) => r.ok).catch(() => false); _pages[page] = { at: Date.now(), p }; return p; };
const twinExists = async (twin) => (twin.base === "home" ? pageExists("home") : twin.base === "marketpulse" ? pageExists("market-pulse") : ((await (twin.base === "insights" ? loadHindiPostSlugs() : twin.base === "podcast" ? loadHindiPodcastSlugs() : loadHindiSlugs())).slugs || []).includes(twin.slug));

export const useHindiAvailable = (slug) => {
  const [ok, setOk] = useState(false);
  useEffect(() => { let live = true; loadHindiSlugs().then((d) => live && setOk((d.slugs || []).includes(slug))); return () => { live = false; }; }, [slug]);
  return ok;
};

export const LangToggle = ({ slug, current, base = "coreservices" }) => {
  const enTo = base ? (slug ? `/${base}/${slug}` : `/${base}`) : "/", hiTo = base ? (slug ? `/hi/${base}/${slug}` : `/hi/${base}`) : "/hi";
  return (
    <div className="inline-flex items-center border border-white/15 font-mono text-[10px] uppercase tracking-wider" data-testid="lang-toggle">
      <Languages className="w-3 h-3 text-gf-primary ml-2" />
      <Link to={enTo} hrefLang="en-IN" onClick={(e) => { const was = getLang(); setLang("en"); if (was !== "en") { e.preventDefault(); window.location.assign(enTo); } }} className={`px-2.5 py-1 ${current === "en" ? "text-gf-primary" : "text-gf-muted hover:text-gf-text"}`} data-testid="lang-toggle-en">English</Link>
      <span className="text-white/20">|</span>
      <Link to={hiTo} hrefLang="hi-IN" onClick={() => setLang("hi")} className={`px-2.5 py-1 ${current === "hi" ? "text-gf-primary" : "text-gf-muted hover:text-gf-text"}`} data-testid="lang-toggle-hi">हिंदी</Link>
    </div>
  );
};

// Site-wide EN | हिंदी switch for the navbar — remembers the reader's choice
export const NavLangSwitch = ({ compact = false, onNavigate }) => {
  const lang = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [tip, setTip] = useState(false);
  const timer = useRef(null);
  const onHi = pathname === "/hi" || pathname.startsWith("/hi/");
  const active = onHi ? "hi" : lang;
  const pick = async (l) => {
    const prev = getLang();
    setLang(l);
    if (l === "en") {
      const target = englishTwin(pathname) || pathname;
      if (prev !== "en") { window.location.assign(target); return; } // full load clears the on-the-fly translation
      if (target !== pathname) navigate(target);
      onNavigate?.();
      return;
    }
    const twin = hindiTwin(pathname);
    if (twin && (await twinExists(twin))) { navigate(twin.to); onNavigate?.(); return; }
    if (!onHi) {
      setTip(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setTip(false), 2600);
    }
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  const cls = (l) => `px-2 py-0.5 transition-colors ${active === l ? "text-gf-bg bg-gf-primary font-bold" : "text-gf-muted hover:text-gf-text"}`;
  return (
    <div className="relative shrink-0" data-testid={compact ? "nav-lang-switch-mobile" : "nav-lang-switch"}>
      <div className={`inline-flex items-center border border-white/15 font-mono uppercase tracking-wider ${compact ? "text-xs" : "text-[10px]"}`} role="group" aria-label="Language">
        <Languages className="w-3 h-3 text-gf-primary ml-1.5" />
        <button type="button" onClick={() => pick("en")} className={`ml-1 ${cls("en")}`} lang="en" data-testid={compact ? "nav-lang-en-mobile" : "nav-lang-en"}>EN</button>
        <button type="button" onClick={() => pick("hi")} className={cls("hi")} lang="hi" data-testid={compact ? "nav-lang-hi-mobile" : "nav-lang-hi"}>हिंदी</button>
      </div>
      {tip && (
        <span lang="hi" role="status" data-testid="nav-lang-unavailable" className="absolute right-0 top-full mt-2 whitespace-nowrap bg-gf-surface border border-white/15 px-2.5 py-1.5 font-body text-[11px] text-gf-text shadow-xl animate-in fade-in">
          यह पेज हिंदी में उपलब्ध नहीं है <span className="text-gf-muted">· सेवाएँ और ब्लॉग हिंदी में खुलेंगे</span>
        </span>
      )}
    </div>
  );
};

// Readers who chose हिंदी land on the Hindi twin automatically (English toggles set the preference back, so no trap)
export const LangRedirect = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (getLang() !== "hi") return;
    const twin = hindiTwin(pathname);
    if (!twin || new URLSearchParams(search).get("lang") === "en") return;
    let live = true;
    twinExists(twin).then((ok) => { if (live && ok) navigate(twin.to + search, { replace: true }); });
    return () => { live = false; };
  }, [pathname, search, navigate]);
  return null;
};
